import type { Engine } from '../core/Engine';
import type { GameModule } from '../core/GameModule';
import type { Camera } from '../renderer/Camera';
import type { FleetState } from '../systems/FleetState';
import type { FleetRenderer } from '../objects/FleetRenderer';
import type { DebugCircleRenderer } from '../objects/DebugCircleRenderer';
import { SHIP_TYPE_DEFS, SHIP_TYPE_COUNT, SHIP_CLASS_NAMES } from '../config/ShipTypes';
import { FootprintDebugTab } from './FootprintDebugTab';

type Tab = 'scale' | 'footprint';

/**
 * Debug tool with two tabs: Scale (all ships in lineup) and Footprint (single ship with radius circle).
 * Toggle with 'P'. Not usable during active travel.
 */
export class ShipPreviewPanel implements GameModule {
  readonly name = 'ShipPreviewPanel';

  private _camera: Camera;
  private _fleet: FleetState;
  private _fleetRenderer: FleetRenderer;
  private _root!: HTMLDivElement;
  private _scaleBox!: HTMLDivElement;
  private _tabBtns: HTMLButtonElement[] = [];
  private _fpTab!: FootprintDebugTab;
  private _active = false;
  private _tab: Tab = 'scale';
  private _savedComp: number[] = [];
  private _savedActive = false;

  constructor(
    camera: Camera, fleet: FleetState,
    fleetRenderer: FleetRenderer, circle: DebugCircleRenderer,
  ) {
    this._camera = camera;
    this._fleet = fleet;
    this._fleetRenderer = fleetRenderer;
    this._fpTab = null!;
    this._buildDOM(circle);
  }

  init(_engine: Engine): void {
    window.addEventListener('keydown', this._onKey);
  }

  update(_dt: number): void {
    if (!this._active) return;
    const t = this._camera.target;
    if (this._tab === 'footprint') {
      this._fleet.update(t.x, 0, t.z, 0, -1);
      this._fleetRenderer.updateFormation();
      this._fpTab.updateView(this._camera, t.x, t.z);
    } else {
      this._fleet.update(t.x, 0.9, t.z, 0, -1);
      this._fleetRenderer.updateFormation();
    }
  }

  dispose(): void {
    this._root.remove();
    window.removeEventListener('keydown', this._onKey);
  }

  private _onKey = (e: KeyboardEvent): void => {
    if (e.key !== 'p' && e.key !== 'P') return;
    if ((e.target as HTMLElement)?.tagName === 'INPUT') return;
    this._toggle();
  };

  private _toggle(): void {
    this._active = !this._active;
    if (this._active) this._activate(); else this._deactivate();
    this._root.style.display = this._active ? '' : 'none';
  }

  private _activate(): void {
    this._savedComp = Array.from(this._fleet.composition);
    this._savedActive = this._fleet.active;
    this._fleet.active = true;
    this._fleetRenderer.setVisible(true);
    this._applyTab();
  }

  private _deactivate(): void {
    this._fleet.setComposition(this._savedComp);
    this._fleet.active = this._savedActive;
    if (!this._savedActive) this._fleetRenderer.setVisible(false);
    this._fleetRenderer.lineup = false;
    this._fleetRenderer.debugCircles = false;
    this._fpTab.deactivate();
  }

  private _switchTab(tab: Tab): void {
    this._tab = tab;
    this._tabBtns[0].style.background = tab === 'scale' ? '#223' : '#111';
    this._tabBtns[1].style.background = tab === 'footprint' ? '#223' : '#111';
    this._scaleBox.style.display = tab === 'scale' ? '' : 'none';
    if (tab === 'footprint') this._fpTab.show(); else this._fpTab.hide();
    if (this._active) this._applyTab();
  }

  private _applyTab(): void {
    if (this._tab === 'scale') {
      const comp = new Array(SHIP_TYPE_COUNT).fill(1);
      this._fleet.setComposition(comp);
      this._fleetRenderer.lineup = true;
      this._fpTab.deactivate();
      this._fleetRenderer.debugCircles = true;
    } else {
      this._fleetRenderer.debugCircles = false;
      this._fpTab.activate();
    }
  }

  private _buildDOM(circle: DebugCircleRenderer): void {
    this._root = document.createElement('div');
    Object.assign(this._root.style, {
      position: 'fixed', bottom: '10px', right: '10px',
      background: 'rgba(10,12,20,0.92)', color: '#bbb', fontFamily: 'monospace',
      fontSize: '12px', padding: '0', borderRadius: '8px', zIndex: '9998',
      display: 'none', border: '1px solid rgba(80,120,255,0.25)',
      width: 'clamp(220px,40vw,320px)',
    });

    const title = document.createElement('div');
    title.textContent = 'Ship Preview [P]';
    Object.assign(title.style, { color: '#6af', fontWeight: 'bold', padding: '10px 14px 6px' });
    this._root.appendChild(title);

    const bar = document.createElement('div');
    Object.assign(bar.style, { display: 'flex', padding: '0 14px', gap: '4px' });
    this._tabBtns.push(this._tabBtn('Scale', 'scale', bar));
    this._tabBtns.push(this._tabBtn('Footprint', 'footprint', bar));
    this._root.appendChild(bar);

    this._scaleBox = document.createElement('div');
    Object.assign(this._scaleBox.style, { padding: '8px 14px 12px' });
    for (let i = 0; i < SHIP_TYPE_COUNT; i++) this._addScaleRow(i);
    this._root.appendChild(this._scaleBox);

    this._fpTab = new FootprintDebugTab(this._fleet, this._fleetRenderer, circle, this._root);

    document.body.appendChild(this._root);
    this._switchTab('scale');
  }

  private _tabBtn(label: string, tab: Tab, parent: HTMLElement): HTMLButtonElement {
    const b = document.createElement('button');
    b.textContent = label;
    b.setAttribute('style',
      'padding:4px 10px;cursor:pointer;border:none;font:inherit;'
      + 'border-radius:4px 4px 0 0;color:#8cf;background:#111;',
    );
    b.addEventListener('click', () => this._switchTab(tab));
    parent.appendChild(b);
    return b;
  }

  private _addScaleRow(idx: number): void {
    const def = SHIP_TYPE_DEFS[idx];
    const header = document.createElement('div');
    header.textContent = `${def.name} (${SHIP_CLASS_NAMES[def.shipClass]})`;
    Object.assign(header.style, { color: '#8cf', fontSize: '11px', marginTop: '4px' });
    this._scaleBox.appendChild(header);

    const row = document.createElement('div');
    Object.assign(row.style, { display: 'flex', alignItems: 'center', gap: '6px' });

    const slider = document.createElement('input');
    slider.type = 'range';
    slider.min = '0.0001';
    slider.max = '0.02';
    slider.step = '0.0001';
    slider.value = String(def.scale);
    slider.style.flex = '1';

    const val = document.createElement('span');
    val.textContent = def.scale.toFixed(4);
    Object.assign(val.style, {
      width: '55px', textAlign: 'right', fontFamily: 'monospace', fontSize: '11px',
    });

    slider.addEventListener('input', () => {
      const v = parseFloat(slider.value);
      SHIP_TYPE_DEFS[idx].scale = v;
      val.textContent = v.toFixed(4);
    });

    row.appendChild(slider);
    row.appendChild(val);
    this._scaleBox.appendChild(row);
  }
}
